import "../styles/Landing.css"

export default function HowItWorksPage() {
  return (
    <div className="howWorksPage">
      <div className="howWorksHeader">
        <div className="howWorksKicker">Using The Site</div>
        <h2 className="howWorksTitle">What is Spine Commons?</h2>
        <p className="howWorksLead">
          The point of Spine Commons is to enable the average patient to understand peer-reviewed, open access publications without
          needing to navigate complex medical jargon. Papers are transformed to use simplified and clarified language, 
          highlight key concepts, and give extra information on more complex topics.
          We programmatically perform transformations, and pull from thousands of related publications to keep the information accurate.
        </p>
      </div>

      <div className="howWorksSteps" aria-label="Site features">

        <section className="howWorksStep">
          <h2 className="howWorksStepTitle">1) Sort and compare publications</h2>
          <p className="howWorksStepBody">
            Browse and sort through papers to identify patterns and disagreements across
            the research, not just within a single paper.
          </p>
        </section>

        <section className="howWorksStep">
          <h2 className="howWorksStepTitle">2) Favorite what you want to revisit</h2>
          <p className="howWorksStepBody">
            Save publications to your favorites so you can quickly return to them later and build a
            personal library as you learn.
          </p>
        </section>

        <section className="howWorksStep">
          <h2 className="howWorksStepTitle">3) Verification is beginning with clinicians</h2>
          <p className="howWorksStepBody">
            We are beginning to verify study translations through collaboration with doctors, using
            clinician review and feedback to improve accuracy and clarity over time.
          </p>
        </section>
      </div>

      <div className="howWorksHeader howWorksHeaderSecondary">
        <div className="howWorksKicker">How It Works</div>
        <h1 className="howWorksTitle">The Technicalities</h1>
        <p className="howWorksLead">
          Our system reads a large library of peer-reviewed publications in our database (112,000) and
          extracts generalizable terms and patterns across studies. Using Natural Language Processing
          (NLP), we simplify the language while referencing what the model learned from the research
          corpus to keep the output accurate.
        </p>
      </div>

      <div className="howWorksSteps" aria-label="How the site works">
        <section className="howWorksStep">
          <h2 className="howWorksStepTitle">1) Maintain a database of 112,000 research papers</h2>
          <p className="howWorksStepBody">
            We store and organize a large library of research publications so they can be searched and
            compared at scale.
          </p>
        </section>

        <section className="howWorksStep">
          <h2 className="howWorksStepTitle">2) Find key terms in the target publication</h2>
          <p className="howWorksStepBody">
            For the paper you’re looking at, we extract the important concepts (conditions, anatomy,
            interventions, outcomes, measurements, and other domain terms).
          </p>
        </section>

        <section className="howWorksStep">
          <h2 className="howWorksStepTitle">3) Use semantic embeddings to find similar terms</h2>
          <p className="howWorksStepBody">
            We represent words/phrases as numerical vectors ("embeddings") and measure distance in that
            vector space (often with cosine similarity). This helps us find related terms even when the
            wording differs.
            {" "}If you would like to read more, see{" "}
            <a
              className="howWorksEmail"
              href="https://aclanthology.org/D14-1162/"
              target="_blank"
              rel="noreferrer"
            >
              GloVe: Global Vectors for Word Representation (EMNLP 2014)
            </a>
            .
          </p>
        </section>

        <section className="howWorksStep">
          <h2 className="howWorksStepTitle">4) Retrieve relevant evidence across other publications</h2>
          <p className="howWorksStepBody">
            Using the key terms and their closest semantic neighbors, we search the database to gather
            related findings from other research publications.
          </p>
        </section>

        <section className="howWorksStep">
          <h2 className="howWorksStepTitle">5) Summarize the target study using NLP + retrieved context</h2>
          <p className="howWorksStepBody">
            Finally, we summarize the target study in simpler language using NLP and the found terms, guided by the
            retrieved context so the explanation stays consistent with what the research corpus
            supports.
          </p>
        </section>
      </div>

      <div className="howWorksFooterNote">
        This page describes the high-level process. Individual features may vary as the database grows.
        <div className="howWorksContact">
          Email{" "}
          <a className="howWorksEmail" href="mailto:jasonpatricksmith00@gmail.com">
            jasonpatricksmith00@gmail.com
          </a>{" "}
          for more information, a more in-depth review, or to get in contact.
        </div>
      </div>
    </div>
  );
}